<template>
  <div class="relative">
    <!-- Input de recherche avec lieu sélectionné -->
    <div v-if="selectedLocation" class="relative">
      <div class="w-full p-3 border border-gray-300 rounded-md bg-gray-50 flex items-center justify-between">
        <div class="flex-1">
          <div class="font-medium text-gray-900 flex items-center gap-2">
            {{ getLocationDisplayName(selectedLocation) }}
            <span v-if="isPublicLocation(selectedLocation)"
              class="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
              Public
            </span>
            <span v-else class="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
              Privé
            </span>
          </div>
          <div class="text-sm text-gray-600">
            {{ getLocationSubtitle(selectedLocation) }}
          </div>
        </div>
        <div class="flex items-center gap-2 ml-4">
          <!-- Bouton d'édition (seulement pour mes lieux) -->
          <button v-if="canEditSelectedLocation && !readOnlyConfig?.isReadOnly" title="Modifier le lieu"
            class="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded" @click="editLocation">
            <PencilIcon class="h-4 w-4" />
          </button>
          <!-- Bouton de suppression -->
          <button v-if="!readOnlyConfig?.isReadOnly" title="Changer de lieu"
            class="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded" @click="resetAll">
            <XIcon class="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Input de recherche -->
    <div v-else class="relative flex items-center">
      <input v-model="searchQuery" type="text" class="w-full p-2 border border-gray-300 rounded-md" :class="{
        'bg-gray-100 cursor-not-allowed': isModelMode
      }" placeholder="Rechercher un lieu..." :disabled="isModelMode" @input="handleSearch" @focus="handleFocus"
        @blur="handleBlur" />

      <!-- Bouton de réinitialisation -->
      <button v-if="(searchQuery || showDropdown) && !readOnlyConfig?.isReadOnly" title="Réinitialiser"
        class="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-red-500 hover:text-red-700"
        @click="resetAll">
        <XIcon class="h-4 w-4" />
      </button>
    </div>

    <!-- Dropdown avec sections séparées -->
    <div v-if="showDropdown && !selectedLocation && !isModelMode"
      class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-y-auto">
      <!-- Bouton créer -->
      <div v-if="!readOnlyConfig?.isReadOnly" title="Créer un nouveau lieu"
        class="p-3 hover:bg-gray-100 cursor-pointer text-blue-600 border-b border-gray-100 flex items-center gap-2"
        @click="openCreator('create')">
        <PlusIcon class="h-4 w-4" />
        <span>Créer un nouveau lieu</span>
      </div>

      <!-- Section : Mes lieux -->
      <div v-if="myLocations.length > 0" class="section">
        <div class="section-header p-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
          <UserIcon class="h-4 w-4 text-blue-600" />
          <span class="text-sm font-medium text-blue-900">Mes lieux ({{ myLocations.length }})</span>
        </div>
        <div v-for="location in myLocations" :key="location.uuid"
          class="p-3 hover:bg-gray-100 cursor-pointer flex justify-between items-center border-l-3 border-blue-500"
          @click="selectLocation(location)">
          <div class="flex-1">
            <div class="font-medium text-gray-900">
              {{ getLocationDisplayName(location) }}
            </div>
            <div class="text-sm text-gray-600">
              {{ getLocationSubtitle(location) }}
            </div>
          </div>
          <div class="flex items-center gap-2 ml-4">
            <button title="Utiliser comme modèle"
              class="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
              @click.stop="useAsTemplate(location)">
              Modèle
            </button>
          </div>
        </div>
      </div>

      <!-- Section : Lieux publics -->
      <div v-if="publicLocations.length > 0" class="section">
        <div class="section-header p-2 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
          <GlobeIcon class="h-4 w-4 text-emerald-600" />
          <span class="text-sm font-medium text-emerald-900">Lieux publics ({{ publicLocations.length }})</span>
        </div>
        <div v-for="location in publicLocations" :key="location.uuid"
          class="p-3 hover:bg-gray-100 cursor-pointer flex justify-between items-center border-l-3 border-emerald-500"
          @click="selectLocation(location)">
          <div class="flex-1">
            <div class="font-medium text-gray-900 flex items-center gap-2">
              {{ getLocationDisplayName(location) }}
              <span class="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                Public
              </span>
            </div>
            <div class="text-sm text-gray-600">
              {{ getLocationSubtitle(location) }}
            </div>
          </div>
          <div class="flex items-center gap-2 ml-4">
            <button title="Utiliser comme modèle"
              class="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
              @click.stop="useAsTemplate(location)">
              Modèle
            </button>
          </div>
        </div>
      </div>

      <!-- Message si aucun lieu -->
      <div v-if="myLocations.length === 0 && publicLocations.length === 0" class="p-4 text-center text-gray-500">
        <div class="flex flex-col items-center gap-2">
          <MapPinIcon class="h-8 w-8 text-gray-400" />
          <span>Aucun lieu trouvé</span>
          <span class="text-xs">Créez-en un nouveau pour commencer</span>
        </div>
      </div>
    </div>

    <!-- Message informatif pour les lieux publics -->
    <div v-if="selectedLocation && isPublicLocation(selectedLocation)"
      class="mt-2 text-xs text-emerald-700 bg-emerald-100 p-2 rounded flex items-center gap-1">
      <GlobeIcon class="h-3 w-3" />
      Lieu public - Vous ne pouvez pas modifier ce lieu
    </div>

    <!-- LocationCreator en modal -->
    <LocationCreator :show="showCreator" :mode="creatorMode" :initial-data="creatorInitialData" :user-id="userId"
      @location-created="handleLocationCreated" @location-updated="handleLocationUpdated"
      @update:show="showCreator = $event" />

    <!-- Composant Notification -->
    <Notification ref="notification" />
  </div>
</template>

<script lang="ts">
import { useLocations } from '@/composables/useAssets';
import { GlobeIcon, MapPinIcon, PencilIcon, PlusIcon, UserIcon, XIcon } from 'lucide-vue-next';
import { computed, defineComponent, ref, watch } from 'vue';
import type { Location } from '../../api/types/entities/location/types';
import Notification from '../Notification.vue';
import LocationCreator from '../creators/LocationCreator.vue';

export default defineComponent({
  name: 'LocationSelector',
  components: {
    XIcon,
    PencilIcon,
    UserIcon,
    GlobeIcon,
    PlusIcon,
    MapPinIcon,
    LocationCreator,
    Notification,
  },
  props: {
    userId: {
      type: String,
      required: true
    },
    initialLocationId: {
      type: String,
      required: false,
      default: ''
    },
    isModelMode: {
      type: Boolean,
      required: false,
      default: false
    },
    // Props du système de cache
    assetsCacheStore: {
      type: Object,
      default: null
    },
    canEditAsset: {
      type: Function,
      default: null
    },
    isPublicAsset: {
      type: Function,
      default: null
    },
    isMyAsset: {
      type: Function,
      default: null
    },
    readOnlyConfig: {
      type: Object,
      required: false,
      default: () => ({ isReadOnly: false, allowNavigation: true, showEditHints: true })
    }
  },
  emits: ['location-selected', 'location-reset'],

  setup(props, { emit }) {
    // Utilisation du composable pour le cache avec filtre 'accessible'
    const {
      allAssets: allLocations,
      isLoading,
      addAsset,
      updateAsset,
      canEditAsset: composableCanEdit,
      isMyAsset: composableIsMyAsset,
      isPublicAsset: composableIsPublicAsset
    } = useLocations({ defaultFilter: 'accessible' });

    // État local
    const searchQuery = ref('');
    const showDropdown = ref(false);
    const selectedLocation = ref<Location | null>(null);
    const notification = ref();

    // État pour LocationCreator
    const showCreator = ref(false);
    const creatorMode = ref<'create' | 'edit'>('create');
    const creatorInitialData = ref<Location | undefined>(undefined);

    // Computed pour séparer mes lieux des publics avec recherche
    const myLocations = computed((): Location[] => {
      const filtered = allLocations.value.filter(location => {
        const isMyAssetFn = props.isMyAsset || composableIsMyAsset;
        return isMyAssetFn(location);
      });

      if (!searchQuery.value) return filtered;

      return filtered.filter(location => {
        const searchableFields = ['site', 'place', 'country', 'address', 'post_code'];
        return searchableFields.some(field => {
          const value = location[field as keyof Location];
          return typeof value === 'string' && value.toLowerCase().includes(searchQuery.value.toLowerCase());
        });
      });
    });

    const publicLocations = computed((): Location[] => {
      const filtered = allLocations.value.filter(location => {
        const isPublicAssetFn = props.isPublicAsset || composableIsPublicAsset;
        return isPublicAssetFn(location);
      });

      if (!searchQuery.value) return filtered;

      return filtered.filter(location => {
        const searchableFields = ['site', 'place', 'country', 'address', 'post_code'];
        return searchableFields.some(field => {
          const value = location[field as keyof Location];
          return typeof value === 'string' && value.toLowerCase().includes(searchQuery.value.toLowerCase());
        });
      });
    });

    // Helper pour vérifier si on peut éditer le lieu sélectionné
    const canEditSelectedLocation = computed((): boolean => {
      if (!selectedLocation.value) return false;

      const canEditFn = props.canEditAsset || composableCanEdit;
      return canEditFn(selectedLocation.value);
    });

    // Helper pour vérifier si un lieu est public
    const isPublicLocation = (location: Location): boolean => {
      const isPublicAssetFn = props.isPublicAsset || composableIsPublicAsset;
      return isPublicAssetFn(location);
    };

    // Fonction pour initialiser le lieu sélectionné à partir de l'ID
    const initializeSelectedLocation = (): void => {
      if (props.initialLocationId && allLocations.value.length > 0) {
        const location = allLocations.value.find(loc => loc.uuid === props.initialLocationId);
        if (location) {
          selectedLocation.value = location;
          searchQuery.value = getLocationDisplayName(location);
        }
      }
    };

    // Watcher pour initialiser le lieu quand les données sont chargées
    watch([() => props.initialLocationId, allLocations], (): void => {
      initializeSelectedLocation();
    }, { immediate: true });

    // Helpers pour l'affichage
    const getLocationDisplayName = (location: Location): string => {
      let displayName = '';
      if (location.site && location.place) {
        displayName = `${location.site} - ${location.place}`;
      } else {
        displayName = location.site || location.place || 'Lieu sans nom';
      }
      
      // Ajouter un indicateur si la location est confidentielle
      if (location.is_confidential) {
        displayName += ' 🔒';
      }
      
      return displayName;
    };

    const getLocationSubtitle = (location: Location): string => {
      const parts = [];
      if (location.address) parts.push(location.address);
      if (location.post_code) parts.push(location.post_code);
      if (location.country) parts.push(location.country);

      return parts.length > 0 ? parts.join(', ') : 'Pas d\'information de localisation';
    };

    // Gestion des événements
    const handleFocus = (): void => {
      if (props.isModelMode || selectedLocation.value) return;
      showDropdown.value = true;
    };

    const handleSearch = (): void => {
      if (props.isModelMode || selectedLocation.value) return;
      showDropdown.value = true;
    };

    const handleBlur = (): void => {
      setTimeout(() => {
        showDropdown.value = false;
      }, 200);
    };

    const selectLocation = (location: Location): void => {
      selectedLocation.value = location;
      searchQuery.value = getLocationDisplayName(location);
      emit('location-selected', location);
      showDropdown.value = false;
    };

    const resetAll = (): void => {
      selectedLocation.value = null;
      searchQuery.value = '';
      showDropdown.value = false;
      emit('location-reset');
    };

    // Gestion du LocationCreator
    const openCreator = (mode: 'create' | 'edit', templateLocation?: Location): void => {
      creatorMode.value = mode;
      creatorInitialData.value = mode === 'edit' ? selectedLocation.value || undefined : undefined;

      // Si on utilise un template, on peut pré-remplir certains champs
      if (templateLocation && mode === 'create') {
        // Le LocationCreator gérera le template via ses props
      }

      showCreator.value = true;
      showDropdown.value = false;
    };

    const editLocation = (): void => {
      if (selectedLocation.value && canEditSelectedLocation.value) {
        openCreator('edit');
      }
    };

    const useAsTemplate = (location: Location): void => {
      // Ouvrir le creator en mode création avec les données du template
      openCreator('create', location);
    };

    const handleLocationCreated = (location: Location): void => {
      // Ajouter au cache
      addAsset(location);

      // Sélectionner le nouveau lieu
      selectLocation(location);

      // Notification
      if (notification.value) {
        notification.value.showNotification(
          `Le lieu "${getLocationDisplayName(location)}" a été créé avec succès`,
          'success'
        );
      }
    };

    const handleLocationUpdated = (location: Location): void => {
      // Mettre à jour le cache
      updateAsset(location);

      // Mettre à jour la sélection si c'est le lieu actuellement sélectionné
      if (selectedLocation.value && selectedLocation.value.uuid === location.uuid) {
        selectedLocation.value = location;
        searchQuery.value = getLocationDisplayName(location);
      }

      // Notification
      if (notification.value) {
        notification.value.showNotification(
          `Le lieu "${getLocationDisplayName(location)}" a été modifié avec succès`,
          'success'
        );
      }
    };

    return {
      searchQuery,
      showDropdown,
      selectedLocation,
      myLocations,
      publicLocations,
      canEditSelectedLocation,
      isPublicLocation,
      isLoading,
      showCreator,
      creatorMode,
      creatorInitialData,
      handleFocus,
      handleSearch,
      handleBlur,
      selectLocation,
      resetAll,
      openCreator,
      editLocation,
      useAsTemplate,
      handleLocationCreated,
      handleLocationUpdated,
      getLocationDisplayName,
      getLocationSubtitle,
      notification
    };
  }
});
</script>

<style scoped>
.border-l-3 {
  border-left-width: 3px;
}

.section-header {
  position: sticky;
  top: 0;
  z-index: 10;
}
</style>